<template>
  <v-dialog
    v-model="dialog"
    scrollable
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-toolbar dense color="primary" dark tabs>
        <v-toolbar-title>
          {{
            selected._id
              ? $t('titles.global.updateModel', { field: $tc('models.team') })
              : $t('titles.global.newModel', { field: $tc('models.team') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn dark icon @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
        <v-tabs
          slot="extension"
          v-model="tab"
          color="primary"
          slider-color="white"
          centered
        >
          <v-tab>{{ $tc('models.team') }}</v-tab>
          <v-tab>{{ $tc('titles.global.planification') }}</v-tab>
          <v-tab :disabled="!team._id">
            {{ $t('fields.global.types.league') }}
          </v-tab>
          <v-tab :disabled="!team._id">
            {{ $tc('titles.global.player', 2) }}
          </v-tab>
          <v-tab :disabled="!team._id">
            {{ $tc('titles.global.trainer', 2) }}
          </v-tab>
          <v-tab :disabled="!team._id">
            {{ $t('fields.global.lineUp') }}
          </v-tab>
        </v-tabs>
      </v-toolbar>
      <v-card-text>
        <v-tabs-items v-model="tab">
          <v-tab-item ripple>
            <tab-information
              ref="tabInformation"
              :team.sync="team"
              :entity-id="entityId"
              :file-img-home.sync="fileHomeImg"
              :file-logo.sync="fileLogo"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <tab-planification
              :team.sync="selected"
              :entity-id="entityId"
              :edit="edit"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <tab-categories v-if="team._id" :team="selected" />
          </v-tab-item>
          <v-tab-item ripple>
            <user-list
              v-if="!dialogBulkCreate || typeRole === 'trainer'"
              :team-id="teamId"
              type-role="player"
              :title="$tc('titles.global.player', 2)"
              @form="setUser"
              @import="() => (dialogImport = true)"
              @bulkCreate="bulkCreate('player')"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <user-list
              v-if="!dialogBulkCreate || typeRole === 'player'"
              :team-id="teamId"
              type-role="trainer"
              :title="$tc('titles.global.trainer', 2)"
              @form="setUser"
              @bulkCreate="bulkCreate('trainer')"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <tab-line-up v-if="team._id" :team-id="team._id" />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-divider />
      <v-card-actions v-if="tab === 0">
        <template v-if="edit">
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
        <v-spacer />
        <template v-if="edit">
          <meta-button type="reset" :disabled="loading" @click="clear" />
          <meta-button
            v-if="!team._id"
            type="create"
            :loading="loading"
            @click="submit()"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
      <user-form
        v-if="dialogUser"
        ref="form"
        :dialog="dialogUser"
        :type-role="typeRole"
        :entity-id="entityId"
        @close="dialogUser = false"
      />
      <dialog-import-users
        v-if="dialogImport"
        :team-id="team._id"
        @close="dialogImport = false"
      />
      <DialogBulkCreateUsers
        v-model="dialogBulkCreate"
        :type-role="typeRole"
        :team-id="selected._id"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import TabCategories from './TabCategories.vue'
import TabInformation from './TabInformation'
import TabLineUp from './TabLineUp'
import TabPlanification from './TabPlanification'
import UserList from '@/components/management/entities/entity-users/components/EntityUserList.vue'
import UserForm from '@/components/management/users/FormManager.vue'
import DialogBulkCreateUsers from '@/components/widgets/dialog/DialogBulkCreateUsers.vue'
import DialogImportUsers from '@/components/widgets/dialog/DialogImportUsers.vue'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'TeamForm',
  components: {
    TabInformation,
    TabPlanification,
    TabLineUp,
    UserList,
    UserForm,
    TabCategories,
    DialogImportUsers,
    DialogBulkCreateUsers,
  },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    entityId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      team: {},
      lineUp: [],
      tab: 0,
      fileLogo: '',
      fileHomeImg: '',
      dialogUser: false,
      dialogImport: false,
      dialogBulkCreate: false,
      typeRole: '',
    }
  },

  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    entityId(val) {
      if (val) {
        this.team.entityId = val
        let params = {
          _id: this.team.entityId,
        }
        this.$store.dispatch('entities/fetchSelectList', params)
      }
    },
    selected: {
      handler: function (val) {
        if (val) this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setObject(this.selected)
    this.fetchRoles()
  },
  computed: {
    ...mapState({
      teamId: (state) => state.teams.idSelected,
      loading: (state) => state.teams.loading,
    }),
    selected: {
      get() {
        return this.$store.state.teams.selected
      },
      set(/* value */) {
        // this.$store.commit('teams/setSelected', value)
      },
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'teams/create',
      setUpdate: 'teams/update',
      fetchTeam: 'teams/fetchOne',
      reset: 'teams/reset',
      uploadFile: 'files/uploadPush',
      fetchUser: 'users/fetchOne',
      resetUser: 'users/reset',
      fetchUserRoles: 'users/fetchUserRoles',
      fetchUserFollowings: 'users/fetchUserFollowings',
      fetchUserSubscriptions: 'users/fetchUserSubscriptions',
      fetchRoles: 'roles/fetchSelectList',
    }),
    submit() {
      this.$nextTick(() => {
        this.$refs.tabInformation.validate().then((valid) => {
          if (!valid) return
          let payload = Object.assign({}, this.team)
          if (
            (payload.categoryId || payload.categoryId === '') &&
            !payload.categoryId.isArray
          )
            payload.categoryId = [payload.categoryId]
          if (this.entityId) payload.entityId = this.entityId

          if (!payload.formation) delete payload.formation
          if (!payload._id) {
            this.setCreate(payload).then((item) => {
              this.uploadFiles(item._id)
              this.fetchTeam(item._id)
            })
          } else {
            this.setUpdate(payload).then((item) => {
              this.uploadFiles(item._id)
              this.fetchTeam(item._id)
            })
          }
        })
      })
    },
    uploadFiles(objectId) {
      if (this.fileLogo) {
        this.uploadFile({
          id: objectId,
          model: 'Teams',
          field: 'logo',
          file: this.fileLogo,
        })
      }

      if (this.fileHomeImg) {
        this.uploadFile({
          id: objectId,
          model: 'Teams',
          field: 'homeImg',
          file: this.fileHomeImg,
        })
      }
    },
    setObject(team) {
      this.team = parseObject(team)
      if (this.entityId) this.team.entityId = this.entityId

      if (this.selected.lineUp) {
        this.lineUp = this.selected.lineUp.map((item) => ({
          userId: item.userId ? item.userId._id : '',
          dorsal: item.dorsal,
          position: item.position,
        }))
      }
      delete this.team.sheet // otherwise it breaks the update in the first tab
    },
    async setUser({ id, typeRole }) {
      this.resetUser()
      this.typeRole = typeRole
      if (id) {
        await this.fetchUser(id)
        this.fetchUserRoles(id)
        this.fetchUserFollowings(id)
        this.fetchUserSubscriptions(id)
      }
      this.dialogUser = true
    },
    bulkCreate(typeRole) {
      this.typeRole = typeRole
      this.dialogBulkCreate = true
    },
    clear() {
      this.$nextTick(() => {
        this.tab = 0
        this.reset()
        if (this.$refs.tabInformation) this.$refs.tabInformation.clear()
      })
    },
    cancel() {
      this.$emit('close')
      this.clear()
    },
  },
}
</script>
